import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectBellNotificationData,
  setBellNotificationData,
} from "../../redux/slices/bellNotificationSlice";
import {
  getNotificationsAndNewOrder,
  readNotification,
} from "../../axios/api/OrderApi";
import { Audio } from "react-loader-spinner";
import { FaTimes } from "react-icons/fa";
import { t } from "i18next";

const NotificationDropdown = ({ setDropdownOpen, ignoreRef }) => {
  const dropdownRef = useRef(null);
  const bellNotificationData = useSelector(selectBellNotificationData);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  // 🔹 Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const response = await getNotificationsAndNewOrder();
        const data = response?.data?.bellNotification;
        if (data) {
          setNotifications(data.data || []);
          dispatch(setBellNotificationData(data));
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [dispatch]);

  // 🔹 Mark a notification as read
  const markAsRead = async (id) => {
    try {
      setLoading(true);
      const response = await readNotification(id);

      if (response?.data?.status) {
        const updated = await getNotificationsAndNewOrder();
        const data = updated?.data?.bellNotification;
        if (data) {
          setNotifications(data.data || []);
          dispatch(setBellNotificationData(data));
        }
      }
    } catch (error) {
      console.error("Error reading notification:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
      const clickedOutsideToggle =
        ignoreRef?.current && !ignoreRef.current.contains(event.target);

      if (clickedOutsideDropdown && clickedOutsideToggle) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setDropdownOpen, ignoreRef]);

  return (
    <div
      ref={dropdownRef}
      className={`fixed inset-0 lg:inset-auto w-full md:w-[440px] 
        block-background-design dark:!bg-secondary_black 
        lg:rounded-xl shadow-lg !border-none py-6 
        ${notifications.length === 0 ? "overflow-auto" : "overflow-y-auto"} 
        z-[99]`}
    >
      <div className="flex items-center justify-between px-6">
        <span className="prose-t20_bold text-primary flex gap-3">
          {t("notifications")}
        </span>
        <FaTimes
          className="h-6 w-6 cursor-pointer"
          onClick={() => setDropdownOpen(false)}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <Audio
            height="80"
            width="80"
            radius="9"
            color="#EE5222"
            ariaLabel="loading"
          />
        </div>
      ) : (
        <ul className="py-1 px-6 md:max-h-[300px] overflow-y-auto">
          {notifications.length === 0 && (
            <li className="text-center py-4 text-gray-500 dark:text-white">
              {t("noNotifications")}
            </li>
          )}
          {notifications.map((item) => (
            <li
              key={item.id}
              className={`flex justify-between items-start mt-3 border rounded-xl p-4 ${
                !item.read ? "block-background-design" : "bg-[#dedede] dark:bg-[#222222]"
              }`}
            >
              <div className="flex flex-col gap-y-1.5 items-start text-start w-[70%]">
                <span className="font-[600] text-[16px] mb-2">
                  {item.name}
                </span>
                <span className="font-[500] text-[16px]">
                  {item.massage}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-[600] text-[16px] mb-2">
                  {item.date}
                </span>
                {!item.read && (
                  <button
                    onClick={() => markAsRead(item.id)}
                    className="font-[500] text-[16px] bg-primary text-white px-5 py-1 rounded-md"
                  >
                    {t("read")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NotificationDropdown;